import BigNumber from "bignumber.js"
import { LineSeriesOption } from "echarts"
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react"
import { Card, Form } from "react-bootstrap"
import { average as findAverage } from "simple-statistics"
import { calcMarkLineOffset } from "../getSpeedChartOptionsHelpers"
import { calcOverUnderPerformance } from "../helpers/calculateSensitivity"
import { getPortStaysData } from "../helpers/getPortStaysData"
import { usePrevetTabStateContext } from "../hooks/prevetContext"
import { VesselStay } from "../hooks/useFetchVesselStays"
import { SegmentSensitivityData } from "../hooks/usePrevetGetSensitivityDataForSegment"

interface PrevetPerformanceExposureCardProps {
  describedLadenSpeed: string
  ladenFuelCon: string
  ladenSeaDays: string
  describedBallastSpeed: string
  ballastFuelCon: string
  ballastSeaDays: string
  segmentSensitivityData: SegmentSensitivityData | undefined
  ladenChartInstance: MutableRefObject<any>
  ballastChartInstance: MutableRefObject<any>
  speedGraphLadenAvgSpeed: number | undefined
  speedGraphBallastAvgSpeed: number | undefined
  portStaysLadenAvgSpeed: number | undefined
  portStaysBallastAvgSpeed: number | undefined
  realLadenMaxSpeed: number | undefined
  realBallastMaxSpeed: number | undefined
  vesselName: string
  defaultFuelCon: number
  setUserLadenVesselSpeed: (speed: string | undefined) => void
  setUserBallastVesselSpeed: (speed: string | undefined) => void
  setHirePrice: Dispatch<SetStateAction<string>>
  hirePrice: string
  setFuelPrice: React.Dispatch<React.SetStateAction<string>>
  fuelPrice: string
}

export function PrevetPerformanceExposureCard({
  describedLadenSpeed,
  ladenFuelCon,
  ladenSeaDays,
  describedBallastSpeed,
  ballastFuelCon,
  ballastSeaDays,
  ladenChartInstance,
  ballastChartInstance,
  speedGraphLadenAvgSpeed,
  speedGraphBallastAvgSpeed,
  portStaysLadenAvgSpeed,
  portStaysBallastAvgSpeed,
  realLadenMaxSpeed,
  realBallastMaxSpeed,
  segmentSensitivityData,
  vesselName,
  defaultFuelCon,
  setUserLadenVesselSpeed,
  setUserBallastVesselSpeed,
  setHirePrice,
  hirePrice,
  setFuelPrice,
  fuelPrice,
}: PrevetPerformanceExposureCardProps) {
  const { setTabStateValue, getTabStateValue } = usePrevetTabStateContext()
  const standardVesselDefaultSpeed = 12

  //get extra time and fuel

  // Keep track of minor customization to grey out the Std Vsl box
  const [isStandardVesselBoxGray, setIsStandardVesselBoxGray] = useState(false)

  // Ref to store previous values
  const previousStateValues = useRef({ hirePrice, fuelPrice })
  const spotIndexName =
    segmentSensitivityData?.benchmark.spot_index_name || "P5TC"

  const defaultHirePrice = segmentSensitivityData?.benchmark.spot_price || ""
  const defaultFuelPrice = segmentSensitivityData?.benchmark.fuel_price || ""

  useEffect(() => {
    if (hirePrice && hirePrice !== previousStateValues.current.hirePrice) {
      setTabStateValue("perfExposureHireRate", hirePrice, undefined)
    }

    if (fuelPrice && fuelPrice !== previousStateValues.current.fuelPrice) {
      setTabStateValue("perfExposureFuelPrice", fuelPrice, undefined)
    }

    previousStateValues.current = { hirePrice, fuelPrice }
  }, [hirePrice, fuelPrice])

  const [performanceExposure, setPerformanceExposure] = useState<
    number | undefined
  >()

  const [vesselComparisonMessage, setVesselComparisonMessage] = useState(
    `Std vsl / ${vesselName}`
  )

  useEffect(() => {
    const tabValue = (
      getTabStateValue("perfExposureHireRate", undefined) + ""
    ).trim()
    if (tabValue !== String(segmentSensitivityData?.benchmark.spot_price)) {
      const newValue =
        tabValue === "" || isNaN(Number(tabValue))
          ? String(segmentSensitivityData?.benchmark.spot_price)
          : tabValue
      setHirePrice(String(newValue))
      setTabStateValue("perfExposureHireRate", newValue, undefined)
    } else {
      setHirePrice(String(segmentSensitivityData?.benchmark.spot_price))
    }
  }, [segmentSensitivityData?.benchmark.spot_price])

  useEffect(() => {
    const tabValue = (
      getTabStateValue("perfExposureFuelPrice", undefined) + ""
    ).trim()
    if (tabValue !== String(segmentSensitivityData?.benchmark.fuel_price)) {
      const newValue =
        tabValue === "" || isNaN(Number(tabValue))
          ? String(segmentSensitivityData?.benchmark.fuel_price)
          : tabValue
      setFuelPrice(String(newValue))
      setTabStateValue("perfExposureFuelPrice", newValue, undefined)
    } else {
      setFuelPrice(String(segmentSensitivityData?.benchmark.fuel_price))
    }
  }, [segmentSensitivityData?.benchmark.fuel_price])

  useEffect(() => {
    const ladenAvgSpeedForCalculation = speedGraphLadenAvgSpeed
      ? speedGraphLadenAvgSpeed
      : portStaysLadenAvgSpeed
    const ballastAvgSpeedForCalculation = speedGraphBallastAvgSpeed
      ? speedGraphBallastAvgSpeed
      : portStaysBallastAvgSpeed

    if (!ladenAvgSpeedForCalculation && !ballastAvgSpeedForCalculation) {
      return
    }

    const performance = calcPerformance({
      describedLadenSpeed,
      describedBallastSpeed,
      speedGraphLadenAvgSpeed: ladenAvgSpeedForCalculation,
      speedGraphBallastAvgSpeed: ballastAvgSpeedForCalculation,
      ladenFuelCon,
      ballastFuelCon,
      hirePrice,
      fuelPrice,
      ladenSeaDays,
      ballastSeaDays,
    })
    setPerformanceExposure(performance)
    setTabStateValue("perfExposureValue", performance, undefined)
  }, [
    describedLadenSpeed,
    describedBallastSpeed,
    portStaysLadenAvgSpeed,
    portStaysBallastAvgSpeed,
    speedGraphLadenAvgSpeed,
    speedGraphBallastAvgSpeed,
    ladenFuelCon,
    ballastFuelCon,
    hirePrice,
    fuelPrice,
    ladenSeaDays,
    ballastSeaDays,
  ])

  function updateChart({
    isLaden,
    speed,
    isReset,
  }: {
    isLaden: boolean
    speed: string
    isReset: boolean
  }) {
    if (
      (isLaden && !speedGraphLadenAvgSpeed) ||
      (!isLaden && !speedGraphBallastAvgSpeed)
    ) {
      return
    }
    const avgSpeed = isLaden
      ? speedGraphLadenAvgSpeed
      : speedGraphBallastAvgSpeed

    const chart = isLaden ? ladenChartInstance : ballastChartInstance
    const opts = chart.current.getOption()

    const maxYAxis = isLaden ? realLadenMaxSpeed : realBallastMaxSpeed

    const yAxis = !maxYAxis // Grow or shrink yAxis depending on user input
      ? [{ ...opts.yAxis[0] }]
      : [
          {
            ...opts.yAxis[0],
            max: [
              Number(speed) > maxYAxis // If user input TC desc > speed graph max
                ? Number(speed) + 1
                : BigNumber(maxYAxis + 1)
                    .decimalPlaces(0, BigNumber.ROUND_HALF_CEIL)
                    .toString(),
            ],
          },
        ]

    // When focusing any input, draw the usr vessel line on speed graph
    chart.current.setOption({
      ...opts,
      series: [
        {
          ...(opts.series as LineSeriesOption[])[0],
        },
        {
          ...(opts.series as LineSeriesOption[])[1],
        },
        {
          ...(opts.series as LineSeriesOption[])[2],
        },
        !isReset
          ? {
              name: `User Vsl`,
              markLine: {
                symbolSize: 7,
                lineStyle: {
                  width: 1,
                  opacity: 0.75,
                },
                data: [
                  {
                    name: "user vessel",
                    yAxis: Number(speed),
                    label: {
                      offset: calcMarkLineOffset({
                        label: Number(speed),
                        compared: avgSpeed as number,
                      }),
                      fontSize: 10,
                      formatter: (params: any) => {
                        return `${BigNumber(params.data.yAxis).toFormat(
                          1
                        )} User Vsl`
                      },
                    },
                  },
                ],
              },
            }
          : {
              name: "User Vsl", // Put this on its own markLine because of symbol: none
              type: "line",
              markLine: {
                lineStyle: {
                  width: 0,
                  opacity: 0,
                },
              },
            },
      ],
      yAxis,
    })
  }

  const updateVesselComparisonMessage = () => {
    setVesselComparisonMessage(`User vsl / ${vesselName}`)
  }

  const { extraTime, extraFuel } = useExtraCalculations(
    parseFloat(describedBallastSpeed),
    parseFloat(ballastFuelCon),
    parseFloat(describedLadenSpeed),
    parseFloat(ladenFuelCon),
    speedGraphLadenAvgSpeed,
    speedGraphBallastAvgSpeed,
    parseFloat(ballastSeaDays),
    parseFloat(ladenSeaDays)
  )

  return (
    <Card className="PrevetContainer_PerformanceExposure w-100 h-100">
      <Card.Body>
        <Card.Title>Performance Exposure</Card.Title>
        <div className="text-muted">
          {" "}
          Std Vsl or {vesselName} avg speed vs Pre-Vetting avg speed
        </div>
        {/* Main body */}
        {performanceExposure && (
          <>
            <h1
              style={{
                color: `${performanceExposure > 0 ? "#e61f2f" : "#00763b"}`,
              }}
              className="mt-5 text-center"
            >
              {performanceExposure > 0
                ? performanceExposure.toLocaleString("en-US")
                : Math.abs(performanceExposure).toLocaleString("en-US")}{" "}
              <DownIcon
                direction={`${performanceExposure > 0 ? "down" : "up"}`}
              />
            </h1>
          </>
        )}
        <div className="my-1 w-75 mx-auto border border-muted" />
        <div className="d-flex w-100 justify-content-around">
          <div className="d-flex gap-1">
            {extraTime !== null &&
            performanceExposure &&
            performanceExposure > 0 ? (
              <>
                <div className="text-danger fs-4">{extraTime.toFixed(1)}d</div>
                <div className="text-muted fs-4">EXTRA Time</div>
              </>
            ) : (
              <>
                <div className="text-muted fs-4">0d</div>
                <div className="text-muted fs-4">EXTRA Time</div>
              </>
            )}
          </div>
          <div className="d-flex gap-1">
            {extraFuel !== null &&
            performanceExposure &&
            performanceExposure > 0 ? (
              <>
                <div className="text-danger fs-4">{extraFuel.toFixed(1)}mt</div>
                <div className="text-muted fs-4">EXTRA Fuel</div>
              </>
            ) : (
              <>
                <div className="text-muted fs-4">0mt</div>
                <div className="text-muted fs-4">EXTRA Fuel</div>
              </>
            )}
          </div>
        </div>

        <div className="d-flex justify-content-between gap-3 mt-5">
          {/* Left STD Vessel */}
          <div className="w-100">
            <div className="text-muted text-center font-italic">Std Vsl</div>
            <table className="w-100 mt-3">
              <tbody>
                <tr>
                  <td className="text-muted">Speed</td>
                  <td className="text-muted text-end">
                    {standardVesselDefaultSpeed} kts
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Fuel Cons</td>
                  <td className="text-muted text-end">{defaultFuelCon} mt/d</td>
                </tr>

                {/* Divider */}
                <tr>
                  <td>
                    <div className="mt-4" />
                  </td>
                </tr>

                <tr>
                  <td className="text-muted">{spotIndexName}</td>
                  <td className="text-muted text-end">
                    $
                    {segmentSensitivityData?.benchmark.spot_price.toLocaleString(
                      "en-US"
                    )}{" "}
                    pdpr
                  </td>
                </tr>
                {/* <div className="my-" /> */}
                <tr>
                  <td className="text-muted">Fuel Price</td>
                  <td className="text-muted text-end">
                    $
                    {segmentSensitivityData?.benchmark.fuel_price.toLocaleString(
                      "en-US"
                    )}{" "}
                    mt/d
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* Divider */}
          <div
            className="border border-gray d-flex align-items-stretch"
            style={{ width: "1px" }}
          />
          {/* Right part */}
          <div className="w-100">
            <div className="text-muted text-center font-italic">
              User Input for calc
            </div>
            {/* Upper part */}
            <div className="d-flex justify-content-between flex-nowrap mt-3 align-items-center">
              <h3 style={{ color: "#ff5c29" }}>B</h3>
              <div>
                <div style={{ color: "#ff5c29" }}>
                  {parseFloat(describedBallastSpeed)}
                </div>
                <div style={{ color: "#ff5c29" }}>
                  {parseFloat(ballastFuelCon)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-muted">kts</div>
                <div className="text-muted">mt/day</div>
              </div>
              <div>
                <div style={{ color: "#001446" }}>
                  {parseFloat(describedLadenSpeed)}
                </div>
                <div style={{ color: "#001446" }}>
                  {parseFloat(ladenFuelCon)}
                </div>
              </div>
              <h3 style={{ color: "#001446" }}>L</h3>
            </div>
            <div className="mt-2 d-flex flex-column gap-1">
              <div className="d-flex justify-content-between align-items-center">
                <div className="text-muted">Hire</div>
                <div className="position-relative w-50">
                  <div className="PrevetSensitivity_DollarSign">$</div>
                  <Form.Control
                    className="bg-light w-100 text-end"
                    aria-label="Amount (to the nearest dollar)"
                    value={
                      hirePrice === "" || isNaN(Number(hirePrice))
                        ? ""
                        : Number(hirePrice).toLocaleString("en-US")
                    }
                    onFocus={(e) => {
                      updateChart({
                        isLaden: true,
                        speed: describedLadenSpeed,
                        isReset: false,
                      })
                      updateChart({
                        isLaden: false,
                        speed: describedBallastSpeed,
                        isReset: false,
                      })
                      updateVesselComparisonMessage()
                      setIsStandardVesselBoxGray(true)
                      setUserLadenVesselSpeed(describedLadenSpeed)
                      setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                    }}
                    onChange={(e) => {
                      if (e.target.value === "") {
                        setHirePrice("")
                      }
                      const regex = new RegExp(
                        /^(?:\d{1,3}(?:[,]\d{1,4})*|\d+)$/
                      )
                      const match = regex.test(e.target.value)
                      if (!match) {
                        return
                      }
                      const num = convertToLocaleNumStringToNumber(
                        e.target.value
                      ) // Convert 15,333 to 15333
                      setHirePrice(String(num))
                      setTabStateValue("perfExposureHireRate", num, undefined)
                    }}
                  />
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <div className="text-muted">Fuel</div>
                <div className="position-relative w-50">
                  <div className="PrevetSensitivity_DollarSign">$</div>
                  <Form.Control
                    className="w-100 bg-light text-end"
                    aria-label="Amount (to the nearest dollar)"
                    value={fuelPrice}
                    onFocus={(e) => {
                      updateChart({
                        isLaden: true,
                        speed: describedLadenSpeed,
                        isReset: false,
                      })
                      updateChart({
                        isLaden: false,
                        speed: describedBallastSpeed,
                        isReset: false,
                      })
                      setUserLadenVesselSpeed(describedLadenSpeed)
                      setUserBallastVesselSpeed(describedBallastSpeed) // Set the User Vsl line in Speed Graph Modal / All Ocean Zones Modal
                      updateVesselComparisonMessage()
                      setIsStandardVesselBoxGray(true)
                    }}
                    onChange={(e) => {
                      setFuelPrice(e.target.value)
                      setTabStateValue(
                        "perfExposureFuelPrice",
                        e.target.value,
                        undefined
                      )
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

function roundToNearestThousand(cost: number) {
  return Math.round(cost / 1000) * 1000
}

function DownIcon({ direction }: { direction: "up" | "down" }) {
  return (
    <svg
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 32 32"
      fill={`${direction === "down" ? "#e61f2f" : "#00763b"}`}
      className={`PrevetSensitivity_Icon--${direction}`}
    >
      <title>play3</title>
      <path d="M6 4l20 12-20 12z"></path>
    </svg>
  )
}

function convertToLocaleNumStringToNumber(numStr: string) {
  return Number(numStr.split(",").join(""))
}

function calcPerformance({
  describedLadenSpeed,
  describedBallastSpeed,
  speedGraphLadenAvgSpeed,
  speedGraphBallastAvgSpeed,
  ladenFuelCon,
  ballastFuelCon,
  hirePrice,
  fuelPrice,
  ladenSeaDays,
  ballastSeaDays,
}: {
  describedLadenSpeed: string
  describedBallastSpeed: string
  speedGraphLadenAvgSpeed?: number
  speedGraphBallastAvgSpeed?: number
  ladenFuelCon: string
  ballastFuelCon: string
  hirePrice: string
  fuelPrice: string
  ladenSeaDays: string
  ballastSeaDays: string
}) {
  let ladenCost
  let ballastCost

  if (speedGraphLadenAvgSpeed) {
    ladenCost =
      calcOverUnderPerformance({
        brokerSpeed: Number(describedLadenSpeed),
        actualSpeed: speedGraphLadenAvgSpeed,
        dailyFuelConsumption: Number(ladenFuelCon),
        dailyHireRate: Number(hirePrice),
        fuelPrice: Number(fuelPrice),
      }).costDeltaUSD * Number(ladenSeaDays)
  }

  if (speedGraphBallastAvgSpeed) {
    ballastCost =
      calcOverUnderPerformance({
        brokerSpeed: Number(describedBallastSpeed),
        actualSpeed: speedGraphBallastAvgSpeed,
        dailyFuelConsumption: Number(ballastFuelCon),
        dailyHireRate: Number(hirePrice),
        fuelPrice: Number(fuelPrice),
      }).costDeltaUSD * Number(ballastSeaDays)
  }

  if (ladenCost && ballastCost) {
    return roundToNearestThousand(ladenCost + ballastCost)
  }

  if (ladenCost) {
    return roundToNearestThousand(ladenCost)
  }
  if (ballastCost) {
    return roundToNearestThousand(ballastCost)
  }
}

export function getAvgSpeedFromPortStays({
  vesselStaysData,
  isLaden,
}: {
  vesselStaysData: VesselStay[] | undefined
  isLaden: boolean
}) {
  if (!vesselStaysData || vesselStaysData.length < 1) {
    return undefined
  }
  const portStaysData = getPortStaysData(vesselStaysData)
  const stays = isLaden ? portStaysData.laden : portStaysData.ballast

  if (!stays || stays.length < 1) {
    return undefined
  }

  return findAverage(stays.map((stay) => stay.avg_speed_mov))
}

export function useExtraCalculations(
  ballastSpeed: number,
  ballastFuelCon: number,
  ladenSpeed: number,
  ladenFuelCon: number,
  speedGraphLadenAvgSpeed: number | undefined,
  speedGraphBallastAvgSpeed: number | undefined,
  ballastSeaDays: number,
  ladenSeaDays: number
) {
  const [extraTime, setExtraTime] = useState<number | null>(null)
  const [extraFuel, setExtraFuel] = useState<number | null>(null)

  useEffect(() => {
    if (
      (speedGraphBallastAvgSpeed &&
        ballastSpeed < speedGraphBallastAvgSpeed &&
        speedGraphLadenAvgSpeed &&
        ladenSpeed < speedGraphLadenAvgSpeed) ||
      ballastFuelCon <= 0 ||
      ladenFuelCon <= 0 ||
      !ballastSpeed ||
      !ladenSpeed ||
      !ladenFuelCon ||
      !ballastFuelCon ||
      !speedGraphLadenAvgSpeed ||
      !speedGraphBallastAvgSpeed
    ) {
      setExtraTime(null)
      setExtraFuel(null)
      return
    }

    const extraTimeBallast = computeExtraTime(
      ballastSpeed,
      speedGraphBallastAvgSpeed,
      ballastSeaDays
    )
    const extraFuelBallast = extraTimeBallast * ballastFuelCon

    const extraTimeLaden = computeExtraTime(
      ladenSpeed,
      speedGraphLadenAvgSpeed,
      ladenSeaDays
    )
    const extraFuelLaden = extraTimeLaden * ladenFuelCon

    setExtraTime(extraTimeBallast + extraTimeLaden)
    setExtraFuel(extraFuelBallast + extraFuelLaden)
  }, [
    ballastSpeed,
    ladenSpeed,
    ballastFuelCon,
    ladenFuelCon,
    speedGraphLadenAvgSpeed,
    speedGraphBallastAvgSpeed,
    ballastSeaDays,
    ladenSeaDays,
  ])

  return { extraTime, extraFuel }
}

function computeExtraTime(speedCon: number, avgSpeed: number, seaDays: number) {
  const lagPerDay = speedCon * 24 - avgSpeed * 24
  const totalDistanceLagged = lagPerDay * seaDays
  const extraTime = totalDistanceLagged / avgSpeed / 24
  return extraTime
}
